'use client'

/**
 * Le formazioni delle due squadre di un set, in una **sola box** su mobile.
 *
 * Il divisorio centrale si trascina per allargare una o l'altra formazione
 * (doppio click = metà e metà); la proporzione si ricorda fra un set e l'altro,
 * così la scelta vale per tutta la partita. Da `md` in su non si usa: là le due
 * formazioni sono già due colonne ai lati del grafico.
 *
 * Riceve i due contenuti **già renderizzati** (server component): qui vive solo
 * la logica del trascinamento.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'

const MIN_SPLIT = 22
const MAX_SPLIT = 78
const STORAGE_KEY = 'lvp.setform.split'

export function SetFormationsBox({ home, away }: { home: ReactNode; away: ReactNode }) {
  const boxRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const [split, setSplit] = useState(50)

  useEffect(() => {
    const saved = Number(window.localStorage.getItem(STORAGE_KEY))
    if (Number.isFinite(saved) && saved >= MIN_SPLIT && saved <= MAX_SPLIT) setSplit(saved)
  }, [])

  function move(clientX: number) {
    const rect = boxRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return
    const pct = ((clientX - rect.left) / rect.width) * 100
    setSplit(Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, pct)))
  }

  function onDown(e: ReactPointerEvent<HTMLDivElement>) {
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
  }
  function onMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (dragging.current) move(e.clientX)
  }
  function onUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging.current) return
    dragging.current = false
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    try { window.localStorage.setItem(STORAGE_KEY, String(Math.round(split))) } catch { /* storage non disponibile */ }
  }

  return (
    <div data-formazioni="">
      <div ref={boxRef} className="flex items-stretch">
        <div data-formazione="home" className="min-w-0" style={{ width: `${split}%` }}>
          {home}
        </div>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Ridimensiona le formazioni"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onDoubleClick={() => setSplit(50)}
          className="group relative mx-1.5 w-1.5 shrink-0 cursor-col-resize touch-none rounded-full bg-[color:var(--league-line)] transition-colors hover:bg-[color:var(--league-brand-solid)]/40"
        >
          <span aria-hidden className="absolute inset-y-3 left-1/2 w-px -translate-x-1/2 bg-[color:var(--league-muted)]/40" />
        </div>

        <div data-formazione="away" className="min-w-0 flex-1">
          {away}
        </div>
      </div>
    </div>
  )
}
